/**
 * Hook for managing PDF export of a video project.
 *
 * Simpler than useVideoExportAll — single atomic job (no per-slide tracking, no assembly).
 *
 * Phases: idle -> submitting -> exporting -> completed -> failed
 *
 * Persists state in DB — restores on page reload via GET /video-export/pdf-state.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { videoExportAPI } from '../services/api';

export type PdfExportPhase = 'idle' | 'submitting' | 'exporting' | 'completed' | 'failed';

export interface PdfExportState {
  phase: PdfExportPhase;
  progress: number; // 0-100
  currentOperation?: string;
  jobId?: string;
  error?: string;
  /** True while loading persisted state from server */
  isRestoring: boolean;
}

const POLL_INTERVAL = 3000;

export function useVideoPdfExport(conversationId?: string) {
  const [state, setState] = useState<PdfExportState>({
    phase: 'idle',
    progress: 0,
    isRestoring: !!conversationId,
  });
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobIdRef = useRef<string | undefined>(undefined);
  const pollErrorCountRef = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ====================================================================
  // Job polling
  // ====================================================================

  const pollJob = useCallback(async () => {
    const jobId = jobIdRef.current;
    if (!jobId) return;

    try {
      const data = await videoExportAPI.getStatus(jobId);
      pollErrorCountRef.current = 0;

      const status = data.status;
      const percentage = data.progress?.percentage ?? 0;
      const currentOperation = data.progress?.currentOperation;

      if (status === 'COMPLETED') {
        stopPolling();
        setState(prev => ({
          ...prev,
          phase: 'completed',
          progress: 100,
          currentOperation: undefined,
          jobId,
        }));
      } else if (status === 'FAILED') {
        stopPolling();
        setState(prev => ({
          ...prev,
          phase: 'failed',
          error: data.error || 'PDF export failed',
          jobId,
        }));
      } else {
        setState(prev => ({
          ...prev,
          progress: percentage,
          currentOperation,
        }));
      }
    } catch (err) {
      pollErrorCountRef.current += 1;
      console.warn(`[pdf-export] Poll error (${pollErrorCountRef.current}/5):`, err);

      if (pollErrorCountRef.current >= 5) {
        stopPolling();
        pollErrorCountRef.current = 0;
      }
    }
  }, [stopPolling]);

  // ====================================================================
  // Restore state from DB on mount
  // ====================================================================

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const restoreState = async () => {
      setState(prev => ({ ...prev, isRestoring: true }));

      try {
        const data = await videoExportAPI.getPdfState(conversationId);
        if (cancelled) return;

        if (data.status === 'none' || !data.jobId) {
          setState(prev => ({ ...prev, isRestoring: false }));
          return;
        }

        const jobId = data.jobId;
        jobIdRef.current = jobId;

        if (data.status === 'COMPLETED') {
          setState({
            phase: 'completed',
            progress: 100,
            jobId,
            isRestoring: false,
          });
        } else if (data.status === 'FAILED') {
          setState({
            phase: 'failed',
            progress: 0,
            jobId,
            error: data.error || undefined,
            isRestoring: false,
          });
        } else if (data.status === 'PENDING' || data.status === 'PROCESSING') {
          setState({
            phase: 'exporting',
            progress: data.progress?.percentage ?? 0,
            currentOperation: data.progress?.currentOperation,
            jobId,
            isRestoring: false,
          });
          // Resume polling
          pollTimerRef.current = setInterval(pollJob, POLL_INTERVAL);
          setTimeout(pollJob, 1000);
        } else {
          setState(prev => ({ ...prev, isRestoring: false }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to restore PDF export state:', err);
          setState(prev => ({ ...prev, isRestoring: false }));
        }
      }
    };

    restoreState();

    return () => {
      cancelled = true;
    };
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================================================================
  // Start export (user-initiated)
  // ====================================================================

  const startPdfExport = useCallback(async (convId: string) => {
    stopPolling();
    jobIdRef.current = undefined;
    pollErrorCountRef.current = 0;

    setState({
      phase: 'submitting',
      progress: 0,
      isRestoring: false,
    });

    try {
      const response = await videoExportAPI.startPdf(convId);

      if (!response.success || !response.jobId) {
        setState({ phase: 'failed', progress: 0, isRestoring: false, error: 'Failed to start PDF export' });
        return;
      }

      jobIdRef.current = response.jobId;

      setState({
        phase: 'exporting',
        progress: 0,
        jobId: response.jobId,
        isRestoring: false,
      });

      pollTimerRef.current = setInterval(pollJob, POLL_INTERVAL);
      setTimeout(pollJob, 1000);
    } catch (err: any) {
      setState({
        phase: 'failed',
        progress: 0,
        isRestoring: false,
        error: err.message || 'Failed to start PDF export',
      });
      throw err;
    }
  }, [stopPolling, pollJob]);

  // ====================================================================
  // Reset
  // ====================================================================

  const reset = useCallback(() => {
    stopPolling();
    jobIdRef.current = undefined;
    setState({ phase: 'idle', progress: 0, isRestoring: false });
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    ...state,
    startPdfExport,
    reset,
    isExporting: state.phase === 'submitting' || state.phase === 'exporting',
  };
}
